import React, { useState } from 'react';
import JSZip from 'jszip';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Image as ImageIcon, Download, ChevronDown, ChevronUp, Loader2, ImageOff, ImagePlus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { downloadInlineImage } from '../utils/inlineImages';
import ImageSearchDialog from './ImageSearchDialog';

const fmtSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const safeName = (img) => {
  let n = (img.name || `image-${img.index}.${img.ext}`).replace(/[\\/:*?"<>|]+/g, '_').trim();
  if (!/\.[a-z0-9]+$/i.test(n)) n += '.' + img.ext;
  return n;
};

const Thumb = ({ img, ticketNumber, onOpen, promoted, onPromote }) => (
  <div data-testid="embedded-image-item" className="group relative border border-[#193b4b] rounded-md overflow-hidden bg-[#081a25]">
    <img
      src={img.src}
      alt={img.name || `Embedded ${img.index}`}
      onClick={() => onOpen(img.index - 1)}
      className="w-full h-28 object-contain bg-white cursor-zoom-in"
    />
    <div className="px-2 py-1.5 text-xs bg-[#081a25] border-t border-[#193b4b]">
      <p data-testid="embedded-image-caption" className="font-medium text-gray-700 truncate" title={img.name}>{img.name}</p>
      <div className="flex items-center justify-between mt-0.5">
        <span className="text-gray-500 truncate">{img.ext.toUpperCase()} · {fmtSize(img.size)}</span>
        <div className="flex items-center gap-2 shrink-0">
          {img.isLikelySignature && (
            promoted ? (
              <button
                type="button"
                data-testid="mark-signature-btn"
                title="Mark as signature/logo (collapse)"
                onClick={() => onPromote(img.key, false)}
                className="text-gray-400 hover:text-gray-700"
              >
                <ImageOff className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                data-testid="not-signature-btn"
                title="Not a signature — always show as a screenshot"
                onClick={() => onPromote(img.key, true)}
                className="text-gray-400 hover:text-purple-700"
              >
                <ImagePlus className="h-3.5 w-3.5" />
              </button>
            )
          )}
          <button
            type="button"
            data-testid="download-embedded-image-btn"
            title="Download"
            onClick={() => downloadInlineImage(img.src, safeName(img))}
            className="text-blue-600 hover:text-blue-800"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

const Grid = ({ images, ticketNumber, onOpen, promotedSet, onPromote }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 items-start">
    {images.map((img) => (
      <Thumb
        key={img.index}
        img={img}
        ticketNumber={ticketNumber}
        onOpen={onOpen}
        promoted={promotedSet.has(img.key)}
        onPromote={onPromote}
      />
    ))}
  </div>
);

// Panel listing a ticket's embedded email images. Real screenshots show first;
// small logo/signature images are collapsed behind a toggle. Techs can promote a
// specific image ("not a signature") and download all images as a zip.
export const EmbeddedImagesPanel = ({ images = [], ticketNumber, onOpen, promotedKeys = [], onPromote, customerId, customerName }) => {
  const [showSignatures, setShowSignatures] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  if (!images || images.length === 0) return null;

  const promotedSet = new Set(promotedKeys || []);
  const isSig = (im) => im.isLikelySignature && !promotedSet.has(im.key);
  const screenshots = images.filter((im) => !isSig(im));
  const signatures = images.filter((im) => isSig(im));

  const handlePromote = (key, promoted) => {
    if (onPromote) onPromote(key, promoted);
  };

  const downloadAll = async () => {
    setZipping(true);
    try {
      const zip = new JSZip();
      const counts = {};
      images.forEach((img) => {
        const b64 = img.src.split(',')[1] || '';
        const base = safeName(img);
        const c = (counts[base] || 0) + 1;
        counts[base] = c;
        const fname = c === 1 ? base : base.replace(/(\.[^.]+)$/, `_${c}$1`);
        zip.file(fname, b64, { base64: true });
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticketNumber || 'images'}-embedded.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${images.length} image${images.length > 1 ? 's' : ''} as a zip`);
    } catch (e) {
      toast.error('Could not build the zip file');
    } finally {
      setZipping(false);
    }
  };

  return (
    <Card data-testid="embedded-images-section">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon className="h-4 w-4 text-purple-600" />
          <h3 className="font-semibold text-sm">Embedded Images ({images.length})</h3>
          <Badge className="bg-purple-100 text-purple-800">From Email</Badge>
          <div className="ml-auto flex items-center gap-3">
            {customerId && (
              <button
                type="button"
                data-testid="panel-image-search-btn"
                onClick={() => setSearchOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                <Search className="h-3.5 w-3.5" />Find past images
              </button>
            )}
            {images.length > 1 && (
              <button
                type="button"
                data-testid="download-all-images-btn"
                onClick={downloadAll}
                disabled={zipping}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                {zipping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {zipping ? 'Zipping...' : 'Download all'}
              </button>
            )}
          </div>
        </div>

        {screenshots.length > 0 ? (
          <Grid images={screenshots} ticketNumber={ticketNumber} onOpen={onOpen} promotedSet={promotedSet} onPromote={handlePromote} />
        ) : (
          <Grid images={signatures} ticketNumber={ticketNumber} onOpen={onOpen} promotedSet={promotedSet} onPromote={handlePromote} />
        )}

        {screenshots.length > 0 && signatures.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              data-testid="show-signatures-btn"
              onClick={() => setShowSignatures((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800"
            >
              {showSignatures ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {showSignatures ? 'Hide' : 'Show'} {signatures.length} logo/signature image{signatures.length > 1 ? 's' : ''}
            </button>
            {showSignatures && (
              <div className="mt-2">
                <Grid images={signatures} ticketNumber={ticketNumber} onOpen={onOpen} promotedSet={promotedSet} onPromote={handlePromote} />
              </div>
            )}
          </div>
        )}
      </CardContent>
      <ImageSearchDialog open={searchOpen} onOpenChange={setSearchOpen} customerId={customerId} customerName={customerName} />
    </Card>
  );
};

export default EmbeddedImagesPanel;
